import { DownOutlined } from '@ant-design/icons';
import { SeasonGroup, Species, Technique, TechniqueFull } from '@omniafishing/core';
import { Button, Dropdown, MenuProps, Radio, RadioChangeEvent, Select } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { useUserPreferences } from '../../hooks/use_user_preferences';
import { OmniaQueryParams } from '../../lib/query_string';
import { WebAnalytics } from '../../lib/web_analytics';
import { getIsMobile } from '../../redux/window';
import { WebAnalyticsLakeClickArgs } from '../waterbody_detail/waterbody_detail_lib/use_waterbody_web_analytics_args';
import { TechniqueFullWithReportCount } from '../waterbody_detail/waterbody_detail_sections/waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import styles from './technique_overflow_menu.less';

const VALUE_ALL_TECHNIQUES = 'all_techniques';

interface TechniqueOverflowMenuProps {
  selectedSeasonGroup: SeasonGroup;
  selectedSpecies: Species;
  selectedTechnique: TechniqueFull;
  topTechniques: TechniqueFullWithReportCount[];
  context: 'waterbody' | 'state';
  webAnalyticsLakeClickArgs?: WebAnalyticsLakeClickArgs;
  onTechniqueChange?: (technique: Technique) => void;
}

export const TechniqueOverflowMenu = (props: TechniqueOverflowMenuProps) => {
  const {
    selectedSeasonGroup,
    selectedSpecies,
    selectedTechnique,
    topTechniques,
    context,
    webAnalyticsLakeClickArgs,
    onTechniqueChange,
  } = props;
  const [invisibilityMap, setInvisibilityMap] = useState<Record<string, boolean>>({});
  const isMobile = useSelector(getIsMobile);
  const buttonGroupRef = useRef(null);
  const { setUserPreferencesTechnique } = useUserPreferences();
  const { replaceQueryString } = useQueryString();
  const { isDesktop } = useResponsive();
  const isLakePage = context === 'waterbody';
  const isStatePage = context === 'state';

  const shouldShowOverflowMenu = useMemo(() => {
    return Object.values(invisibilityMap).some((v) => v === true);
  }, [invisibilityMap]);

  const handleIntersection: IntersectionObserverCallback = (entries) => {
    const updatedInvisibilityMap: typeof invisibilityMap = {};
    entries.forEach((entry) => {
      const targetId = (entry.target as HTMLElement).dataset.targetid;
      updatedInvisibilityMap[targetId] = !entry.isIntersecting;
    });

    setInvisibilityMap((prevTechniques) => {
      const prevTechniquesInTopTechniques: typeof invisibilityMap = {};
      const topTechniqueNames = topTechniques.map((t) => t.name);

      for (const techniqueName in prevTechniques) {
        if (Object.prototype.hasOwnProperty.call(prevTechniques, techniqueName)) {
          if (topTechniqueNames.includes(techniqueName)) {
            prevTechniquesInTopTechniques[techniqueName] = prevTechniques[techniqueName];
          }
        }
      }
      return {
        ...prevTechniquesInTopTechniques,
        ...updatedInvisibilityMap,
      };
    });
  };

  useEffect(() => {
    let observer: IntersectionObserver = null;
    if (isMobile) {
      if (observer) {
        observer.disconnect();
      }
    } else {
      observer = new IntersectionObserver(handleIntersection, {
        root: buttonGroupRef.current,
        threshold: 1,
      });

      Array.from(buttonGroupRef.current.children).forEach((item: HTMLElement) => {
        if (item.dataset.targetid && observer) {
          observer.observe(item);
        }
      });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [
    isMobile,
    selectedSeasonGroup.name,
    selectedSpecies.name,
    topTechniques.map((t) => t.name).join(', '),
  ]);

  const onRadioButtonChange = (e: RadioChangeEvent) => {
    const selectedTechniqueName = e.target.value;
    if (selectedTechniqueName === VALUE_ALL_TECHNIQUES) {
      replaceQueryString({ [OmniaQueryParams.technique]: null });
      onTechniqueChange?.(null);
    } else {
      replaceQueryString({ [OmniaQueryParams.technique]: selectedTechniqueName });
      onTechniqueChange?.(topTechniques.find((t) => t.name === selectedTechniqueName));
    }
    setUserPreferencesTechnique(selectedTechniqueName);
    WebAnalytics.contentFiltered('technique', selectedTechniqueName);
    if (isLakePage) {
      WebAnalytics.lakePageClick('[donut].(technique_selector)', webAnalyticsLakeClickArgs);
    }
  };

  const overflowEntries = Object.entries(invisibilityMap);
  const isSelectedTechniqueInOverflowSelect = overflowEntries.some((t) => {
    const [techniqueName, visibility] = t;
    return techniqueName === selectedTechnique?.name && visibility === true;
  });

  const items: MenuProps['items'] = topTechniques.map((technique) => {
    if (invisibilityMap[technique.name]) {
      return {
        key: technique.name,
        label: `${technique.display_name_alternate}`,
      };
    }
    return null;
  });

  const mobileSelectOptions = topTechniques.map((t, i) => {
    return {
      label: `${t.display_name_alternate} #${i + 1}`,
      value: t.name,
    };
  });

  const statePageMobileSelectOptions = [
    {
      label: 'All Techniques',
      value: VALUE_ALL_TECHNIQUES,
    },
    ...mobileSelectOptions,
  ];

  return (
    <>
      {isDesktop && (
        <div className={styles.wrapper}>
          <Radio.Group
            /* the 'all_techniques' value is a departure in parity for the two contexts.
            however this value will work on lake page b/c resetting to null sets the top waterbody technique */
            value={selectedTechnique?.name || VALUE_ALL_TECHNIQUES}
            buttonStyle="solid"
            onChange={(e) => onRadioButtonChange(e)}
            className={styles.list}
            ref={buttonGroupRef}
          >
            {isStatePage && (
              <div className={styles.listItem}>
                <Radio.Button value={VALUE_ALL_TECHNIQUES}>
                  <p>
                    <strong>All Techniques</strong>
                  </p>
                </Radio.Button>
              </div>
            )}
            {topTechniques.map((t, i) => {
              return (
                <div
                  data-targetid={t.name}
                  className={classNames(styles.listItem, {
                    [styles.invisible]: invisibilityMap[t.name],
                  })}
                  key={t.name}
                >
                  <Radio.Button value={t.name}>
                    <p>
                      <strong>{t.display_name_alternate}</strong> #{i + 1}
                    </p>
                  </Radio.Button>
                </div>
              );
            })}
          </Radio.Group>

          {shouldShowOverflowMenu && (
            <div
              className={classNames(styles.overflowDropdown, {
                [styles.overflowDropdown__notSelected]: !isSelectedTechniqueInOverflowSelect,
              })}
            >
              <Dropdown
                menu={{
                  items,
                  selectable: true,
                  onSelect: ({ key }) => {
                    const techniqueName = key;
                    replaceQueryString({ [OmniaQueryParams.technique]: techniqueName });
                    setUserPreferencesTechnique(techniqueName);
                    WebAnalytics.contentFiltered('technique', techniqueName);
                    onTechniqueChange?.(topTechniques.find((t) => t.name === techniqueName));
                  },
                  selectedKeys: [selectedTechnique?.name],
                }}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Button type="text">
                  <DownOutlined />
                  {isSelectedTechniqueInOverflowSelect ? (
                    <span className={styles.selectedItemDropdown}>
                      <strong>{selectedTechnique?.display_name_alternate}</strong>
                    </span>
                  ) : (
                    <span className={styles.dropDownLabel}>Other Techniques</span>
                  )}
                </Button>
              </Dropdown>
            </div>
          )}
        </div>
      )}

      {isMobile && (
        <div className={styles.techniqueSelectContainer}>
          <p>Select a Technique</p>
          <Select
            value={selectedTechnique?.name || VALUE_ALL_TECHNIQUES}
            onChange={(techniqueName) => {
              if (techniqueName === VALUE_ALL_TECHNIQUES) {
                replaceQueryString({ [OmniaQueryParams.technique]: null });
                onTechniqueChange?.(null);
              } else {
                replaceQueryString({ [OmniaQueryParams.technique]: techniqueName });
                onTechniqueChange?.(topTechniques.find((t) => t.name === techniqueName));
              }
              setUserPreferencesTechnique(techniqueName);
              WebAnalytics.contentFiltered('technique', techniqueName);
            }}
            options={isStatePage ? statePageMobileSelectOptions : mobileSelectOptions}
          />
        </div>
      )}
    </>
  );
};
